// src/utils/brandHelper.ts

import { useTranslation } from 'react-i18next';
import type { Branch } from '~/types/new_types';

/**
 * Get the localized name for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant name
 */
export const getBranchName = (branch: Branch | null, language: string): string => {
	if (language === 'ar' && branch?.ar?.name) {
		return branch?.ar?.name;
	}
	return branch?.name || '';
};

/**
 * Get the localized description for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant description
 */
export const getBranchDescription = (branch: Branch, language: string): string | null => {
	if (language === 'ar' && branch.ar?.description) {
		return branch.ar.description;
	}
	return branch.description ?? null;
};

/**
 * Get the localized address for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant address with each line separated
 */
export const getBranchAddress = (branch: Branch, language: string): string | null => {
	if (language === 'ar' && branch.ar?.address) {
		const addressData = branch.ar.address;
		// Handle both string and array formats
		if (Array.isArray(addressData)) {
			return addressData.join('\n');
		}
		return addressData;
	}
	const addressData = branch.address;
	if (Array.isArray(addressData)) {
		return addressData.join('\n');
	}
	return addressData ?? null;
};

/**
 * Hook to get all localized branch data
 * @param branch - The branch object
 * @returns Object with localized name, description, and address
 */
export const useLocalizedBranch = (branch: Branch | null) => {
	const { i18n } = useTranslation();

	if (!branch) {
		return {
			name: '',
			description: null,
			address: null
		};
	}

	return {
		name: getBranchName(branch, i18n.language),
		description: getBranchDescription(branch, i18n.language),
		address: getBranchAddress(branch, i18n.language)
	};
};

/**
 * Get localized branch data without using hooks (for use in non-component contexts)
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Object with localized name, description, and address
 */
export const getLocalizedBranchData = (branch: Branch | null, language: string) => {
	if (!branch) {
		return {
			name: '',
			description: null,
			address: null
		};
	}

	return {
		name: getBranchName(branch, language),
		description: getBranchDescription(branch, language),
		address: getBranchAddress(branch, language)
	};
};